import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Award,
  Gift,
  Printer,
  Signpost,
  Check,
  Play,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import BlockProse from "@/components/blocks/renderers/BlockProse";

// Full-featured homepage hero block: badge, tri-part headline, rich-text intro,
// primary/secondary CTAs, optional Watch-Video button (opens modal), and a
// right-hand image with an optional invisible overlay-link. Preserves the
// interactive tagline row parsing from the original Hero component so admin
// text like "18 Years Serving the Promotional Products, Print, and Sign
// Industries" still renders as pill badges automatically.

const iconForIndustry = (label) => {
  const s = (label || "").toLowerCase();
  if (s.includes("promo")) return Gift;
  if (s.includes("print")) return Printer;
  if (s.includes("sign")) return Signpost;
  return Award;
};

function HeroVideoModal({ open, onClose, videoUrl }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="hero-video-modal"
      className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          data-testid="hero-video-close"
          className="absolute -top-11 right-0 text-white/80 hover:text-white flex items-center gap-1.5 text-sm font-medium"
        >
          Close <X className="h-4 w-4" />
        </button>
        <div className="rounded-2xl overflow-hidden bg-black">
          <video
            src={videoUrl}
            controls
            autoPlay
            playsInline
            className="w-full h-auto"
            data-testid="hero-video-element"
          />
        </div>
      </div>
    </div>
  );
}

function parseTaglineForBadges(tagline) {
  const m = tagline.match(
    /^\s*(\d{1,3}(?:\+)?)\s*Years?\s+Serving(?:\s+the)?\s+([^.]+?)(?:\s+Industries?)?\s*$/i
  );
  if (!m) return null;
  const yearsLabel = `${m[1]} Years`;
  const raw = m[2].replace(/,\s+and\s+/i, ", ").replace(/\s+and\s+/i, ", ");
  const industries = raw.split(/,\s*/).map((s) => s.trim()).filter(Boolean);
  if (industries.length < 2 || industries.length > 4) return null;
  return { yearsLabel, industries };
}

function parseTaglineForCheckList(tagline) {
  if (!tagline || tagline.length > 220) return null;
  if (/years?\s+serving/i.test(tagline)) return null;
  const parts = tagline
    .split(/\s*[|•✓·]\s*|,\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length < 3 || parts.length > 6) return null;
  return parts;
}

export default function HeroOverlayImageBlock({ data }) {
  const [videoOpen, setVideoOpen] = useState(false);
  const d = data || {};

  const badgeText = d.badge_text || "";
  const headlineLead = d.headline_lead || "";
  const headlineHighlight = d.headline_highlight || "";
  const headlineTrail = d.headline_trail || "";
  const intro = d.intro || "";
  const tagline = d.tagline || "";
  const ctaPrimaryLabel = d.cta_primary_label || "Request a Demo";
  const ctaPrimaryHref = d.cta_primary_href || "/contact";
  const ctaSecondaryLabel = d.cta_secondary_label || "";
  const ctaSecondaryHref = d.cta_secondary_href || "";
  const videoUrl = d.video_url || "";
  const videoLabel = d.video_label || "Watch Video";
  const image = d.image || "";
  const imageAlt = d.image_alt || "";
  const imageHref = d.image_href || "";

  const parsedTagline = parseTaglineForBadges(tagline);
  const checkList = !parsedTagline ? parseTaglineForCheckList(tagline) : null;

  const imgSrc = image
    ? image.startsWith("http")
      ? image
      : `${process.env.REACT_APP_BACKEND_URL || ""}${image}`
    : "";

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-white pt-28 pb-6 sm:pt-32 sm:pb-8 lg:pt-36 lg:pb-10"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 right-[10%] w-[500px] h-[500px] bg-sky-50 rounded-full blur-[120px] opacity-60" />
        <div className="absolute bottom-0 left-[5%] w-[400px] h-[400px] bg-sky-100/40 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-6 items-center">
          {/* Left copy */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            {badgeText && (
              <div className="inline-flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-full px-4 py-1.5 mb-4">
                <span className="w-2 h-2 rounded-full bg-sky-500 animate-pulse" />
                <span className="text-xs font-semibold text-sky-700 tracking-wide uppercase">
                  {badgeText}
                </span>
              </div>
            )}

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
              {headlineLead}
              {headlineHighlight ? (
                <>
                  {headlineLead ? " " : ""}
                  <span className="text-sky-600">{headlineHighlight}</span>
                </>
              ) : null}
              {headlineTrail ? ` ${headlineTrail}` : ""}
            </h1>

            {intro && (
              <div className="mt-4 max-w-xl">
                <BlockProse
                  value={intro}
                  proseClassName="prose prose-slate prose-p:text-slate-600 prose-a:text-sky-600 text-base lg:text-lg"
                  plainClassName="text-base lg:text-lg text-slate-600 leading-relaxed whitespace-pre-line"
                />
              </div>
            )}

            {/* CTAs */}
            <div className="relative z-10 mt-6 flex flex-col sm:flex-row sm:flex-nowrap sm:items-center gap-3 sm:gap-4">
              {ctaPrimaryLabel && (
                <Link to={ctaPrimaryHref}>
                  <Button
                    data-testid="hero-request-demo-btn"
                    className="btn-premium bg-sky-600 hover:bg-sky-700 text-white rounded-full px-8 py-3 h-12 text-sm font-semibold shadow-lg shadow-sky-600/25"
                  >
                    {ctaPrimaryLabel}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              )}
              {ctaSecondaryLabel && (
                <Link to={ctaSecondaryHref || "/"}>
                  <Button
                    variant="outline"
                    data-testid="hero-explore-btn"
                    className="btn-premium rounded-full px-8 py-3 h-12 text-sm font-semibold border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    {ctaSecondaryLabel}
                  </Button>
                </Link>
              )}
              {videoUrl && (
                <Button
                  type="button"
                  onClick={() => setVideoOpen(true)}
                  data-testid="hero-watch-video-btn"
                  className="btn-premium bg-sky-600 hover:bg-sky-700 text-white rounded-full px-8 py-3 h-12 text-sm font-semibold shadow-lg shadow-sky-600/25"
                >
                  <Play className="mr-2 h-4 w-4 fill-white text-white" />
                  {videoLabel}
                </Button>
              )}
            </div>

            {/* Trust badges / checklist / plain tagline */}
            {parsedTagline ? (
              <div
                data-testid="hero-tagline"
                className="mt-7 lg:mt-9 flex flex-wrap items-center gap-2 sm:gap-2.5"
              >
                <div
                  data-testid="hero-tagline-years-pill"
                  className="inline-flex items-center gap-2 rounded-full bg-sky-600 pl-1.5 pr-3.5 py-1 shadow-md shadow-sky-600/25"
                >
                  <span className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center">
                    <Award className="h-3.5 w-3.5 text-white" strokeWidth={2.4} />
                  </span>
                  <span className="text-xs sm:text-sm font-bold text-white tracking-wide">
                    {parsedTagline.yearsLabel}
                  </span>
                </div>
                <span className="w-1 h-1 rounded-full bg-slate-300" aria-hidden />
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                  {parsedTagline.industries.map((label) => {
                    const Icon = iconForIndustry(label);
                    return (
                      <span
                        key={label}
                        data-testid={`hero-tagline-industry-${label
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "-")}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 pl-2 pr-3 py-1 shadow-sm"
                      >
                        <Icon className="h-3.5 w-3.5 text-sky-600" strokeWidth={2.2} />
                        <span className="text-[11px] sm:text-xs font-semibold text-slate-700 whitespace-nowrap">
                          {label}
                        </span>
                      </span>
                    );
                  })}
                </div>
              </div>
            ) : checkList ? (
              <div
                data-testid="hero-tagline"
                className="mt-6 lg:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 max-w-md"
              >
                {checkList.map((item) => (
                  <span
                    key={item}
                    data-testid={`hero-tagline-check-${item
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")}`}
                    className="inline-flex items-center gap-1.5"
                  >
                    <Check className="h-4 w-4 text-sky-600 shrink-0" strokeWidth={3} />
                    <span className="text-[13px] sm:text-sm font-semibold text-slate-700 whitespace-nowrap">
                      {item}
                    </span>
                  </span>
                ))}
              </div>
            ) : tagline ? (
              <div className="mt-6 lg:mt-8 flex items-center gap-3" data-testid="hero-tagline">
                <div className="w-8 sm:w-10 h-px bg-sky-600 shrink-0" />
                <p className="text-xs sm:text-sm md:text-base font-semibold text-slate-700 tracking-wide">
                  {tagline}
                </p>
              </div>
            ) : null}
          </motion.div>

          {/* Right image with optional overlay link */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative lg:col-span-7"
          >
            {imgSrc ? (
              <div className="relative w-full" style={{ paddingTop: "56.92%" }}>
                <img
                  src={imgSrc}
                  alt={imageAlt || ""}
                  className="absolute inset-0 w-full h-full"
                  data-testid="hero-presentation-showcase-image"
                />
                {imageHref && (
                  <Link
                    to={imageHref}
                    aria-label={imageAlt || "Learn more"}
                    data-testid="hero-presentation-text-link"
                    className="absolute rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                    style={{ top: "3%", left: "1.5%", width: "44%", height: "80%" }}
                  />
                )}
              </div>
            ) : null}
          </motion.div>
        </div>
      </div>

      <HeroVideoModal
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        videoUrl={videoUrl}
      />
    </section>
  );
}
